import { useState } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useDashboardState, useEnvironment, useGetTenants } from '../../hooks';
import { ENVIRONMENT_LABELS } from '../../constants';
import { ChartCard } from '../../charts/ChartCard';
import { WidgetControls } from '../../charts/WidgetControls';
import { MetricWidget } from './MetricWidget';
import { AwsCostTrend, HealthSummary, KpiStrip } from '../../charts/widgets';
import { metricWidget, titleFor, type WidgetSpec } from '../../dashboard/spec';
import styles from './Dashboard.module.scss';

const Grid = WidthProvider(GridLayout);

const newId = (): string => `w_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const PRESETS: {
  label: string;
  make: (id: string) => WidgetSpec;
  size: { w: number; h: number; minW: number; minH: number };
}[] = [
  {
    label: 'Chart',
    make: (id) => metricWidget(id, 'venues', 'tenant', 'hbar'),
    size: { w: 4, h: 6, minW: 3, minH: 4 },
  },
  {
    label: 'Publishes over time',
    make: (id) => metricWidget(id, 'publishes', 'day', 'line'),
    size: { w: 8, h: 6, minW: 4, minH: 4 },
  },
  {
    label: 'Headline numbers',
    make: (id) => ({
      id,
      kind: 'kpis',
      metric: 'venues',
      group_by: 'tenant',
      range: '30d',
      chart: 'hbar',
      limit: null,
    }),
    size: { w: 12, h: 3, minW: 6, minH: 3 },
  },
  {
    label: 'Tenant health',
    make: (id) => ({
      id,
      kind: 'health',
      metric: 'failures',
      group_by: 'tenant',
      range: '30d',
      chart: 'hbar',
      limit: null,
    }),
    size: { w: 4, h: 6, minW: 3, minH: 5 },
  },
  {
    label: 'AWS cost by account',
    make: (id) => ({
      id,
      kind: 'aws',
      metric: 'venues',
      group_by: 'tenant',
      range: '90d',
      chart: 'bar',
      limit: null,
    }),
    size: { w: 8, h: 6, minW: 4, minH: 4 },
  },
];

export const Dashboard = () => {
  const { environment } = useEnvironment();
  const { data: tenants = [], isLoading: tenantsLoading } = useGetTenants();
  const { state, loading, saving, setLayout, updateWidget, removeWidget, addWidget, reset } =
    useDashboardState();
  const [addOpen, setAddOpen] = useState(false);

  const add = (preset: (typeof PRESETS)[number]) => {
    addWidget(preset.make(newId()), preset.size);
    setAddOpen(false);
  };

  const render = (spec: WidgetSpec) => {
    if (spec.kind === 'kpis') {
      return tenantsLoading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <KpiStrip tenants={tenants} />
      );
    }
    if (spec.kind === 'health') {
      return tenantsLoading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <HealthSummary tenants={tenants} />
      );
    }
    if (spec.kind === 'aws') {
      return <AwsCostTrend />;
    }
    return <MetricWidget spec={spec} />;
  };

  const subtitleFor = (spec: WidgetSpec): string | undefined => {
    if (spec.kind === 'health') {
      return 'Failures in the last 24 hours and stale publishes';
    }
    if (spec.kind === 'aws') {
      return 'USD, unblended, Cost Explorer';
    }
    return undefined;
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{ENVIRONMENT_LABELS[environment]}</p>
          <h1 className={styles.title}>Control room</h1>
        </div>
        <div className={styles.actions}>
          <span className={styles.saveState} role="status">
            {saving ? 'Saving…' : 'Saved to your account'}
          </span>
          <div className={styles.addWrap}>
            <button
              type="button"
              className={styles.primary}
              onClick={() => setAddOpen((open) => !open)}
              aria-expanded={addOpen}
            >
              + Add chart
            </button>
            {addOpen ? (
              <ul className={styles.menu} role="menu">
                {PRESETS.map((preset) => (
                  <li key={preset.label}>
                    <button type="button" role="menuitem" onClick={() => add(preset)}>
                      {preset.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <button type="button" className={styles.ghost} onClick={reset}>
            Reset
          </button>
        </div>
      </header>
      {loading || !state ? (
        <div className={styles.loading}>Loading your dashboard…</div>
      ) : (
        <Grid
          className={styles.grid}
          layout={state.layout}
          cols={12}
          rowHeight={64}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          isDraggable
          isResizable
          draggableHandle=".widget-drag-handle"
          draggableCancel=".widget-no-drag"
          onLayoutChange={setLayout}
          compactType="vertical"
        >
          {state.layout.map(({ i }) => {
            const spec = state.widgets.find(({ id }) => id === i);
            if (!spec) {
              return <div key={i} />;
            }
            return (
              <div key={i}>
                <ChartCard
                  title={titleFor(spec)}
                  subtitle={subtitleFor(spec)}
                  onRemove={() => removeWidget(i)}
                  controls={
                    spec.kind === 'metric' ? (
                      <WidgetControls spec={spec} onChange={(patch) => updateWidget(i, patch)} />
                    ) : undefined
                  }
                >
                  {render(spec)}
                </ChartCard>
              </div>
            );
          })}
        </Grid>
      )}
    </div>
  );
};
